import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PlayerCreateRequest, PlayerTeamAssignmentRequest, PlayerUpdateRequest } from '@y-sports/api-client';
import { PlayerDataService } from '../services/PlayerDataService';
import { userPeopleQueryKeys } from './useUserPeopleData';

export const playerQueryKeys = {
  all: ['players'] as const,
  management: (email: string) => [...playerQueryKeys.all, 'management', email] as const,
  detail: (playerId?: string) => [...playerQueryKeys.all, 'detail', playerId ?? 'unknown'] as const,
};

export function usePlayerManagementDataQuery(email?: string) {
  return useQuery({
    queryKey: playerQueryKeys.management(email ?? 'missing'),
    queryFn: () => PlayerDataService.getPlayerManagementData(email!),
    enabled: !!email,
    refetchOnWindowFocus: false,
  });
}

export function usePlayerDetailQuery(playerId?: string) {
  return useQuery({
    queryKey: playerQueryKeys.detail(playerId),
    queryFn: () => PlayerDataService.getPlayerDetail(playerId!),
    enabled: Boolean(playerId),
  });
}

function invalidatePlayerManagement(queryClient: ReturnType<typeof useQueryClient>, email?: string) {
  if (!email) {
    return;
  }

  queryClient.invalidateQueries({ queryKey: playerQueryKeys.management(email) });
  queryClient.invalidateQueries({ queryKey: userPeopleQueryKeys.inventory(email) });
}

export function useCreatePlayerMutation(email?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PlayerCreateRequest) => PlayerDataService.createPlayer(payload),
    onSuccess: () => invalidatePlayerManagement(queryClient, email),
  });
}

export function useUpdatePlayerMutation(email?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ playerId, payload }: { playerId: string; payload: PlayerUpdateRequest }) =>
      PlayerDataService.updatePlayer(playerId, payload),
    onSuccess: () => invalidatePlayerManagement(queryClient, email),
  });
}

export function useDeletePlayerMutation(email?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (playerId: string) => PlayerDataService.deletePlayer(playerId),
    onSuccess: () => invalidatePlayerManagement(queryClient, email),
  });
}

export function useAssignPlayerToTeamMutation(email?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ playerId, payload }: { playerId: string; payload: PlayerTeamAssignmentRequest }) =>
      PlayerDataService.assignPlayerToTeam(playerId, payload),
    onSuccess: () => invalidatePlayerManagement(queryClient, email),
  });
}

export function useRemovePlayerFromTeamMutation(email?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ playerId, teamId }: { playerId: string; teamId: string }) =>
      PlayerDataService.removePlayerFromTeam(playerId, teamId),
    onSuccess: () => invalidatePlayerManagement(queryClient, email),
  });
}